import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import InputTextField from "src/components/common/Form/InputTextField";

interface UpdateDisplayNameModalProps {
  onClose: () => void;
  shouldShow: boolean;
}

interface UpdateDisplayNameModalValues {
  display_name: string;
}

const updateDisplayNameModalSchema = yup.object().shape({
  display_name: yup.string().required("Display name is required"),
});

export default function UpdateDisplayNameModal({ onClose, shouldShow }: UpdateDisplayNameModalProps) {
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<UpdateDisplayNameModalValues>({
    resolver: yupResolver(updateDisplayNameModalSchema),
    defaultValues: {
      display_name: "",
    },
  });

  const userApi = useUserApi();
  const userAction = useAppUserAction();
  const toast = useToaster();

  const onSubmit = (values: UpdateDisplayNameModalValues) => {
    if (!values.display_name?.trim()) {
      toast.error("Please enter a display name");
      return;
    }

    setIsLoading(true);

    const params = {
      display_name: values.display_name.trim(),
    };

    userApi.callUpdateDisplayName(
      params,
      (message: any, _resp: any) => {
        setIsLoading(false);
        onClose();
        userAction.updateDisplayName(params.display_name);
        toast.success(message || "Display name updated successfully");
        reset();
      },
      (message: any) => {
        setIsLoading(false);
        toast.error(message || "Failed to update display name");
      }
    );
  };

  const handleClose = () => {
    reset();
    setIsLoading(false);
    onClose();
  };

  return (
    <>
      {shouldShow && (
        <>
          <div className="modal fade show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content update-displayname-modal">
                <div className="modal-header border-0">
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={handleClose}
                    aria-label="Close"
                    disabled={isLoading}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  {/* User Icon */}
                  <div className="update-displayname-icon mb-3">
                    <div className="user-icon">
                      <i className="fas fa-user-edit"></i>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="update-displayname-title mb-2">Update Display Name</h2>
                  <p className="update-displayname-subtitle mb-4">Choose how others see you</p>

                  {/* Display Name Input */}
                  <div className="displayname-input-container mb-3">
                    <div className="input-group">
                      <span className="input-group-text displayname-prefix">
                        <i className="fas fa-tag"></i>
                      </span>
                      <InputTextField
                        fieldName="display_name"
                        control={control}
                        errors={errors}
                        placeholder="Enter your display name"
                        fieldType="text"
                      />
                    </div>
                  </div>

                  {/* Update Button */}
                  <button
                    className="btn btn-primary update-displayname-btn w-100"
                    onClick={handleSubmit(onSubmit)}
                    type="button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      "Update Display Name"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}
